import { HttpClient } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { BehaviorSubject, Observable, of } from "rxjs";
import {ITableState} from "./crud-table/models/table.model";
import {PaginatorState} from "./crud-table/models/paginator.model";
import {GroupingState} from "./crud-table/models/grouping.model";
import {BaseService} from "./base.service";
import {UtilityService} from "./utility.service";
import {ProfileService} from "./profile.service";
import {catchError, finalize,share,shareReplay,tap} from "rxjs/operators";
import { ROUTES } from "../constants";

const DEFAULT_STATE: ITableState = {
	filter: {},
	paginator: new PaginatorState(),
	sorting: {
		column: "createdAt",
		direction: "desc",
	},
	searchTerm: "",
	grouping: new GroupingState(),
};

@Injectable({
	providedIn: "root",
})
export class SubscriptionService extends BaseService<any> {
	currentOrgId: string;
	_isManaged$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	_baseURL: string;

	constructor(
		private utilityService: UtilityService,
		private profileService: ProfileService,
		@Inject(HttpClient) http
	) {
		super(http);
	}

	private _total$: BehaviorSubject<number> = new BehaviorSubject(0);

	get total$() {
		return this._total$.asObservable();
	}

	get isManaged$() {
		return this._isManaged$.asObservable();
	}

	private _sub$ = new BehaviorSubject({});

	get sub$() {
		return this._sub$.asObservable();
	}

	private _activeSubscription$ = new BehaviorSubject({});

	get activeSubscription$() {
		return this._activeSubscription$.asObservable();
	}

	private _subs$: BehaviorSubject<any[]> =
		new BehaviorSubject([]);

	get subs$() {
		return this._subs$.asObservable();
	}

	getDataForUrl() {
		if (this.profileService.profileType === "buyer") {
			this._baseURL = this.API_URL + this.VERSION_V1 + this.API_ROUTES.BUYER.ORGANISATION.BASE_URL;
		} else {
			this._baseURL = this.API_URL + this.VERSION_V1 + this.API_ROUTES.SUPPLIER.ORGANISATION.BASE_URL;
		}

		this.currentOrgId = this.profileService.getCurrentOrganization();
	}

	createSubscription(params): Observable<any> {
		this.getDataForUrl();
		this._isLoading$.next(true);
		const url = this._baseURL + "/" + this.currentOrgId + ROUTES.SUBSCRIPTIONS_CREATE;
		return this.create(params, url);
	}

	getById(id: string): Observable<any> {
		this.getDataForUrl();
		const url = this._baseURL + "/" + this.currentOrgId + ROUTES.SUBSCRIPTIONS_ROUTE + "/" + id;
		return this.getDetails(url);
	}

	deleteById(id: string): Observable<any> {
		this.getDataForUrl();
		const url = this._baseURL + "/" + this.currentOrgId + ROUTES.SUBSCRIPTIONS_ROUTE + "/" + id;
		return this.deleteItem(url);
	}

	/**
	 * Get API Keys List
	 * @param id :string Id of tender
	 * @returns
	 */
	fetchList(): void {
		this.getDataForUrl();
		const url = this._baseURL + "/" + this.currentOrgId + ROUTES.SUBSCRIPTIONS_SEARCH;

		this._isLoading$.next(true);
		this._errorMessage.next("");
		const request = this.post(url, this.tableState)
			.pipe(
				shareReplay(),
				share(),
				tap((res: any) => {
					this._subs$.next(res.items);
					this._total$.next(res.total);

					if(res.total > 0 ){
						this._isManaged$.next(true);
					} else {
						this._isManaged$.next(false);
					}
				}),
				catchError((err) => {
					this._errorMessage.next(err);
					return of({
						items: [],
						total: 0,
					});
				}),
				finalize(() => {
					this._isLoading$.next(false);
				})
			)
			.subscribe();
		this._subscriptions.push(request);
	}

	getAvailableSubscriptions() {
		this.getDataForUrl();
		const url = this._baseURL +	"/" + this.currentOrgId + ROUTES.SUBSCRIPTIONS_AVAILABLE;
		return this.getDetails(url);
	}

	getActiveSubscriptions() {
		this.getDataForUrl();
		const url = this._baseURL +	"/" + this.currentOrgId + ROUTES.SUBSCRIPTIONS_ACTIVE;
		this.getDetails(url).subscribe((response) => {
			if(response) {
				this._isManaged$.next(true);
				this._activeSubscription$.next(response);
			} else {
				this._isManaged$.next(false);
				this._activeSubscription$.next({});
			}
		});
	}

	getOrganizationActiveSubscriptions(): Observable<any> {
		this.getDataForUrl();
		const url = this._baseURL + "/" + this.currentOrgId + ROUTES.SUBSCRIPTIONS_ACTIVE;
		return this.getDetails(url).pipe(
			tap((response) => {
				if (response) {
					this._isManaged$.next(true);
					this._activeSubscription$.next(response);
				} else {
					this._isManaged$.next(false);
					this._activeSubscription$.next({});
				}
			})
		);
	}
}
